import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { BasicTable, ConfirmDialog, Dropdown, MessageBar } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { GetCustomerManagedGroupsRequest } from '../../../../../admin-center/gen/custom_group_management/custom_group_management_pb';
import { AddMemberRequest } from '../../../../../admin-center/gen/user_management/user_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { messageBarContainerStyles } from '../../../common/common.styles';
import { buildUserContext } from '../../../common/utils';
import { StatusLabel, UserRoleOptionKey } from '../../../constants';
import { confirmationTableStyles, confirmationTextStyles } from '../UserManagement.styles';

interface IAddAsAdminConfirmationProps {
  users: { userName: string; userEmail: string; userStatus: StatusLabel }[];
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess: () => Promise<void>;
  envId: string | null;
}

interface IAddAsAdminConfirmationListProps {
  users: { userName: string; userEmail: string; userStatus: StatusLabel }[];
  hasInvalidUsers: boolean;
  selectedGroups: string[];
  groupOptions: IDropdownOption[];
  onGroupChange: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => void;
}

export default function AddAsAdminConfirmation({
  users,
  showDialog,
  setShowDialog,
  onSubmitSuccess,
  envId,
}: IAddAsAdminConfirmationProps) {
  const [isAddAsAdminPending, setIsAddAsAdminPending] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [showMessageBar, setShowMessagebar] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>('');
  const [customGroups, setCustomGroups] = useState<string[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([UserRoleOptionKey.admin]);
  const service = useAdminCenterService();

  const appUser = useUser();

  const filteredUsers = users.filter((user) => appUser?.name !== user.userEmail);

  useEffect(() => {
    const fetchCustomGroups = async () => {
      try {
        const req: GetCustomerManagedGroupsRequest = {};
        const response = await service.getCustomerManagedGroupsRequest(req);
        setCustomGroups(response.userGroups || []);
      } catch (error) {
        console.error('Error fetching custom groups:', error);
      }
    };
    if (showDialog) {
      fetchCustomGroups();
    }
  }, [showDialog, service, appUser]);

  const groupOptions: IDropdownOption[] = [
    { key: UserRoleOptionKey.admin, text: 'Admins' },
    ...customGroups.map((group) => ({ key: group, text: group })),
  ];

  const onGroupChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (!option) return;

    setSelectedGroupKeys((prevKeys: string[]) => {
      if (option.selected) {
        return [...prevKeys, option.key as string];
      } else {
        return prevKeys.filter((k) => k !== option.key);
      }
    });
  };

  const handleAddAsAdmin = async () => {
    if (selectedGroupKeys.length === 0) {
      setResponseMessage('⚠️ Please select at least one group');
      setIsSuccess(false);
      setShowMessagebar(true);
      return;
    }

    if (!envId && selectedGroupKeys.some((key) => key === UserRoleOptionKey.admin || key === UserRoleOptionKey.user)) {
      setResponseMessage('❌ Error: Environment ID not available. Please refresh the page.');
      setIsSuccess(false);
      setShowMessagebar(true);
      setShowDialog(false);
      return;
    }

    setIsAddAsAdminPending(true);
    const usersList = filteredUsers.map((user) => {
      const [firstName, ...lastNameParts] = user.userName.split(' ');
      return {
        firstName: firstName || '',
        lastName: lastNameParts.join(' ') || '',
        email: user.userEmail,
      };
    });

    try {
      const userContext = await buildUserContext(appUser);
      const groupNames = selectedGroupKeys.map((key) => {
        if (key === UserRoleOptionKey.admin) return `hadc_${envId}_admin`;
        if (key === UserRoleOptionKey.user) return `hadc_${envId}_user`;
        return key; // custom group names are used as-is
      });

      const request: AddMemberRequest = {
        members: usersList,
        groupNames: groupNames,
        modifiedBy: appUser?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.addMembers(request);

      if (response.statusUpdate) {
        setResponseMessage(response.statusUpdate);
        setIsSuccess(true);
      } else {
        setResponseMessage('❌ Error: Failed to add users to selected group(s)');
        setIsSuccess(false);
      }
      onSubmitSuccess();
    } catch (error) {
      setResponseMessage(`❌ Network error: ${error}`);
    } finally {
      setIsAddAsAdminPending(false);
      setShowDialog(false);
      setShowMessagebar(true);
      setSelectedGroupKeys([UserRoleOptionKey.admin]); // Reset to default
    }
  };

  return (
    <>
      {showMessageBar && (
        <div style={messageBarContainerStyles}>
          <MessageBar
            timeout={4000}
            onDismiss={() => setShowMessagebar(false)}
            messageBarType={isSuccess ? MessageBarType.success : MessageBarType.error}
          >
            {responseMessage}
          </MessageBar>
        </div>
      )}
      <ConfirmDialog
        hidden={!showDialog}
        title="Add Users to Group(s)"
        confirmationButtonText="Confirm"
        dismissalButtonText="Cancel"
        onConfirm={handleAddAsAdmin}
        onDismiss={() => {
          setShowDialog(false);
          setSelectedGroupKeys([UserRoleOptionKey.admin]); // Reset on dismiss
        }}
        content={
          <AddAsAdminConfirmationList
            users={filteredUsers}
            hasInvalidUsers={users.length !== filteredUsers.length}
            selectedGroups={selectedGroupKeys}
            groupOptions={groupOptions}
            onGroupChange={onGroupChange}
          />
        }
        minWidth="500px"
        maxWidth="800px"
        confirmationButtonDisabled={isAddAsAdminPending || !filteredUsers.length || selectedGroupKeys.length === 0}
      />
    </>
  );
}

function AddAsAdminConfirmationList({
  users,
  hasInvalidUsers,
  selectedGroups,
  groupOptions,
  onGroupChange,
}: IAddAsAdminConfirmationListProps) {
  const dropdownContainerStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  return (
    <div>
      {users.length > 0 ? (
        <>
          <div style={dropdownContainerStyle}>
            <Dropdown
              label="Select Group(s)"
              placeholder="Select groups to add users to"
              options={groupOptions}
              multiSelect
              selectedKeys={selectedGroups}
              onChange={onGroupChange}
              required={true}
            />
          </div>
          <p style={confirmationTextStyles}>The following users will be added to the selected group(s):</p>
          <div style={confirmationTableStyles}>
            <BasicTable
              columns={[
                {
                  key: 'userName',
                  fieldName: 'userName',
                  name: 'User',
                  minWidth: 250,
                },
                {
                  key: 'userEmail',
                  fieldName: 'userEmail',
                  name: 'Email',
                  minWidth: 250,
                },
              ]}
              items={users}
            />
          </div>
          {hasInvalidUsers && (
            <p style={confirmationTextStyles}>
              <b>Note:</b> You cannot add yourself to groups using this method.
            </p>
          )}
        </>
      ) : (
        <>
          <p style={confirmationTextStyles}>You cannot add yourself to groups using this method.</p>
        </>
      )}
    </div>
  );
}
